import { createFileRoute, Link, redirect, useSearch } from "@tanstack/react-router";
import { LogIn } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { apiRequest, AuthRequiredError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const SearchSchema = z
  .object({
    return_to: z.string().optional(),
    // Machine code set by the OIDC callback on a terminal error (it redirects
    // here instead of dumping JSON). Mapped to a localized message below.
    sso_error: z.string().optional(),
  })
  .strict();

export const Route = createFileRoute("/login")({
  validateSearch: SearchSchema.parse,
  // Already authenticated → bounce into the app instead of showing the login page.
  beforeLoad: async ({ search }) => {
    let authed = false;
    try {
      await apiRequest<unknown>("/auth/sessions");
      authed = true;
    } catch (err) {
      if (!(err instanceof AuthRequiredError)) throw err;
    }
    if (authed) {
      const rt = search.return_to;
      const target = rt && rt.startsWith("/") && rt !== "/system" && rt !== "/login" ? rt : "/";
      throw redirect({ to: target });
    }
  },
  component: LoginPage,
});

/**
 * Tenant login page (public, no auth guard).
 *
 * ADR-0097 item 1: the root `/login` is the TENANT entry — Keycloak OIDC for
 * customers/resellers. Local password + SAML Google (admin/superuser) live on
 * `/system` (see routes/system.tsx), which FortiWeb restricts to internal IPs.
 *
 * Vedi: apps/api/src/dpanel_api/auth/routes/oidc.py + docs/adr/0097-*.md §D5.
 */
export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const { return_to, sso_error } = useSearch({ from: "/login" });

  function startOidcLogin(): void {
    const target = encodeURIComponent(return_to ?? window.location.pathname);
    window.location.assign(`/api/v1/auth/oidc/login?return_to=${target}`);
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-3 pb-2">
          <div className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-primary">
            din<span className="italic text-accent">o</span>va
          </div>
          <CardTitle>{t("login.title")}</CardTitle>
          <CardDescription>{t("login.tenant_description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {sso_error && (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {t(`login.sso_error.${sso_error}`, { defaultValue: t("login.sso_error.failed") })}
            </p>
          )}
          <Button type="button" onClick={startOidcLogin} className="w-full gap-2" size="lg">
            <LogIn className="size-4" />
            {t("login.oidc_button")}
          </Button>
          <p className="text-center text-xs text-muted-foreground">{t("login.oidc_hint")}</p>

          <p className="pt-2 text-center text-xs text-muted-foreground">
            <Link
              to="/system"
              search={return_to ? { return_to } : undefined}
              className="underline underline-offset-2 hover:text-foreground"
            >
              {t("login.system_link")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </main>
  );
}
